import { useEffect, useMemo, useState } from 'react';
import orderBy from 'lodash/orderBy';
import { useRouter, useSearchParams } from 'next/navigation';
import { format } from 'date-fns';

import { PAGE_ROUTES, TEMPLATE_BASE_PATH } from '@/constants';
import {
  AuthContainer,
  Button,
  IconSvg,
  Link,
  Spinner,
  useAuth,
} from '@/components';
import { useCustomerOrderHistory } from '@/data';
import { currencyFormat, formatAddress, scrollToTop } from '@/utils';
import { Order as OrderType } from '__generated__/graphql';

import type { OrderFilterType } from './constants';
import { isCompletedOrder, isCurrentOrder } from './constants';
import { OrdersFilterBar } from './Orders.component';
import { Order } from './OrderDetail';

const Orders = () => {
  const [orderFilter, setOrderFilter] = useState<OrderFilterType>('all');
  const { isAuth, email } = useAuth();
  const { data, hasMore, isLoadingMore, loadMore, orders } =
    useCustomerOrderHistory({
      skip: !isAuth,
      variables: {
        email,
      },
    });
  const baseOrders = orders as OrderType[];
  const router = useRouter();
  const query = useSearchParams();
  const [selectedOrderId, setSelectedOrderId] = useState<string | undefined>(
    undefined,
  );
  const [expandReorder, setExpandReorder] = useState(false);

  const filteredOrders = useMemo(() => {
    if (orderFilter === 'all') return baseOrders;
    return baseOrders.filter(order => {
      const status = order?.status ?? null;
      if (orderFilter === 'current') return isCurrentOrder(status);
      if (orderFilter === 'completed') return isCompletedOrder(status);
      return false;
    });
  }, [baseOrders, orderFilter]);

  const handleViewDetail = (entity: string, focusReorder = false) => {
    setSelectedOrderId(entity);
    setExpandReorder(focusReorder);
    router?.push(`${PAGE_ROUTES.ACCOUNT}?section=orders&orderId=${entity}`, {
      scroll: false,
    });
    scrollToTop();
  };

  const handleGoBack = () => {
    setSelectedOrderId(undefined);
    setExpandReorder(false);
    router?.replace(`${PAGE_ROUTES.ACCOUNT}?section=orders`, {
      scroll: false,
    });
    scrollToTop();
  };

  useEffect(() => {
    setSelectedOrderId(query?.get('orderId') as string);
  }, [query]);

  const renderRow = (
    address,
    created_at,
    delivery,
    entity,
    price,
    order_number,
  ) => {
    const addressLabel = formatAddress({ address, format: 'short' });

    return (
      <tr className="order__table_link" key={entity}>
        <td data-label="Order placed">
          <span className="cell_label">Date placed</span>
          <span className="cell_value">
            {format(new Date(created_at), 'PP')}
          </span>
        </td>
        <td data-label="Total">
          <span className="cell_label">Total</span>
          <span className="cell_value">
            <b>$ {currencyFormat(price)}</b>
          </span>
        </td>
        <td data-label="Ship To">
          <span className="cell_label">
            {delivery?.delivery ? 'Ship to' : 'Pickup at'}
          </span>
          {address?.address1 || address?.city ? (
            <span className="cell_value">{addressLabel}</span>
          ) : (
            <span className="cell_value">Not Assigned yet</span>
          )}
        </td>
        <td data-label="Order #">
          <span className="cell_label">Order #</span>
          <span className="cell_value">
            <span onClick={() => handleViewDetail(entity)} role="button">
              {order_number ?? 'Not Assigned yet'}
            </span>
          </span>
        </td>
        <td className="orders__btn-actions">
          <span className="cell_label"></span>
          <div className="orders__btn-actions__buttons">
            <Button
              ariaLabel="See order details"
              color="secondary"
              onClick={() => handleViewDetail(entity)}
              size="sm"
            >
              See Details
            </Button>
            <Button
              ariaLabel="Reorder from this order"
              color="primary"
              onClick={() => handleViewDetail(entity, true)}
              size="sm"
            >
              <IconSvg aria-hidden="true" name="refresh" />
              Reorder
            </Button>
          </div>
        </td>
      </tr>
    );
  };

  if (selectedOrderId) {
    return <Order expandReorder={expandReorder} goBack={handleGoBack} />;
  }

  return (
    <div className="orders">
      <OrdersFilterBar
        onOrderFilterChange={setOrderFilter}
        orderFilter={orderFilter}
      />
      <AuthContainer>
        <div
          className="tab__list_orders_active"
          id="tab-all-orders"
          key="tab-all-orders"
          role="tabpanel"
        >
          {!filteredOrders.length && !data && (
            <div className="orders__history-loading ">
              <Spinner>Loading...</Spinner>
            </div>
          )}
          {!filteredOrders.length && !!data && (
            <div className="orders__history-blank">
              <p className="orders__list_blanck">
                {baseOrders.length === 0 ? (
                  <>
                    No recent orders.{' '}
                    <Link
                      aria-label="Add some items to get started"
                      className="orders__link"
                      href={TEMPLATE_BASE_PATH.SHOP}
                    >
                      Add some items to get started.
                    </Link>
                  </>
                ) : (
                  <>No orders match this filter.</>
                )}
              </p>
            </div>
          )}
          {filteredOrders.length > 0 && (
            <table>
              <tbody>
                {orderBy(filteredOrders, ['created_at'], ['desc']).map(
                  ({
                    created_at,
                    delivery_address,
                    delivery_details,
                    entity_id,
                    total_price,
                    treez_order_number,
                  }) =>
                    renderRow(
                      delivery_address,
                      created_at,
                      delivery_details,
                      entity_id,
                      total_price,
                      treez_order_number,
                    ),
                )}
              </tbody>
            </table>
          )}
          {hasMore && filteredOrders.length > 0 && (
            <Button
              ariaLabel="Load More"
              className="orders__load-more"
              color="primary"
              loading={isLoadingMore}
              onClick={loadMore}
              size="sm"
            >
              Load More
            </Button>
          )}
        </div>
      </AuthContainer>
    </div>
  );
};

export { Orders };
